import BusinessIcon from '@mui/icons-material/Business';
import WorkOutline from '@mui/icons-material/WorkOutline';
import { resolveProjectBadgeSwatch } from '@/shared/data/projectBadges';
import { getPlanChipTone } from '@/shared/plans/planChipTone';
import { r, s, typographyTokens } from '@/theme/tokens';
import { Chip } from '@/ui';
import { Box } from '@/ui/primitives';
import { LockIcon } from '@/ui/primitives/icons';
import {
  buildOrganisationSettingsHref,
  buildPlanSettingsHref,
} from '../../features/organisations/utils/organisationSettingsRoute';
import { buildProjectSettingsHref } from '../../features/projects/utils/projectSettingsRoute';
import type { AuthSession } from '@/auth/types';
import type { NavItem } from '@/components/sidebarTypes';
import type { NotificationContextValue } from '@/providers/notificationContext';
import type { FeatureFlagResolveResult } from '@/shared/data/featureFlags';
import type { PlanBadge } from '@/shared/plans/planStatus';
import type { SidebarItem, SidebarSection } from '@/ui/composites/SidebarNav';
import type { OrganisationDto, PlanCode, ProjectDto } from '@flowqa/shared';
import type { ReactElement } from 'react';

type BuildSidebarContext = {
  session: AuthSession | null;
  navItems: NavItem[];
  activePath: string;
  onNavigate: (path: string) => void;
  preloadRoute?: (path: string) => Promise<void> | null;
  featureFlags?: FeatureFlagResolveResult['flags'] | null;
  addOns?: {
    deliveryEnabled: boolean;
    operationsEnabled: boolean;
    codeEnabled: boolean;
  } | null;
  isAdmin: boolean;
  isSuperuser: boolean;
  planName: string | null;
  planCode: PlanCode | null;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  setSelectedOrganisationId: (value: string | null) => void;
  setSelectedProjectId: (value: string | null) => void;
  notify: NotificationContextValue['notify'];
  projectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  allProjectsOptions: Array<
    Pick<ProjectDto, 'id' | 'name' | 'slug' | 'badgeChars' | 'badgeColor' | 'badgeEnabled'> & {
      locked: boolean;
      lockReason: string | null;
    }
  >;
  organisationsOptions: Array<Pick<OrganisationDto, 'id' | 'name' | 'slug'>>;
  canCreateProject: boolean;
  lockProjectSelector: boolean;
  projectLabel: string;
  projectBadge: { chars: string; color: ProjectDto['badgeColor'] } | null;
  projectLocked: boolean;
  planBadge: PlanBadge | null;
  onAddProject: () => void;
  onAddOrganisation: () => void;
};

const isSelected = (
  itemPath: string,
  secondaryPath: string | undefined,
  selectedPath: string,
): boolean => {
  if (itemPath === '/') {
    return selectedPath === '/';
  }
  if (selectedPath.startsWith(itemPath)) {
    return true;
  }
  if (secondaryPath && selectedPath.startsWith(secondaryPath)) {
    return true;
  }
  return false;
};

const buildProjectBadge = (projectBadge: BuildSidebarContext['projectBadge']): ReactElement => (
  <Box
    sx={{
      width: s('2xl'),
      height: s('2xl'),
      borderRadius: r('sm'),
      backgroundColor: resolveProjectBadgeSwatch(projectBadge?.color ?? 'teal'),
      color: 'common.white',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    <Box
      component="span"
      sx={{
        display: 'block',
        lineHeight: 1,
        fontSize: `${typographyTokens.captionSmall.fontSize} !important`,
        fontWeight: `${typographyTokens.label.fontWeight} !important`,
        pl: s('xxs'),
        color: 'common.white',
        letterSpacing: typographyTokens.captionSmall.letterSpacing,
        textTransform: typographyTokens.captionSmall.textTransform,
      }}
    >
      {projectBadge?.chars ?? '?'}
    </Box>
  </Box>
);

const shouldShowProjectBadge = (
  projectLabel: string,
  projectBadge: BuildSidebarContext['projectBadge'],
): boolean => projectLabel !== 'All Projects' && Boolean(projectBadge);

const buildProjectLockAdornment = (): ReactElement => (
  <Box
    sx={{
      width: s('2xl'),
      height: s('2xl'),
      borderRadius: r('sm'),
      backgroundColor: 'action.disabledBackground',
      color: 'text.secondary',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    <LockIcon sx={{ fontSize: 14 }} />
  </Box>
);

const buildSidebarItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem => ({
  id: item.key,
  label: item.label,
  href: item.path,
  icon: item.icon,
  active: isSelected(item.path, item.secondaryPath, selectedPath),
  preload: preloadRoute ? (): Promise<void> | null => preloadRoute(item.path) : undefined,
});

const buildSecondaryItem = (
  item: NavItem,
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem | null => {
  const secondaryPath = item.secondaryPath;
  if (!item.secondary || !secondaryPath) return null;
  return {
    id: `${item.key}-secondary`,
    label: item.secondary,
    href: secondaryPath,
    active: isSelected(secondaryPath, undefined, selectedPath),
    preload: preloadRoute ? (): Promise<void> | null => preloadRoute(secondaryPath) : undefined,
  };
};

const pickNavItems = (
  items: NavItem[],
  keys: string[],
  selectedPath: string,
  preloadRoute: BuildSidebarContext['preloadRoute'],
): SidebarItem[] => {
  const byKey = new Map(items.map((item) => [item.key, item]));
  return keys.flatMap((key) => {
    const match = byKey.get(key);
    if (!match) return [];
    const primaryItem = buildSidebarItem(match, selectedPath, preloadRoute);
    const secondaryItem = buildSecondaryItem(match, selectedPath, preloadRoute);
    return secondaryItem ? [primaryItem, secondaryItem] : [primaryItem];
  });
};

const firstDefined = <T,>(values: Array<T | null | undefined>): T | null =>
  values.find((value): value is T => value !== null && value !== undefined) ?? null;

const buildWorkspaceSection = ({
  activePath,
  onNavigate,
  selectedOrganisationId,
  setSelectedOrganisationId,
  setSelectedProjectId,
  notify,
  projectsOptions,
  allProjectsOptions,
  organisationsOptions,
  canCreateProject,
  lockProjectSelector,
  projectLabel,
  projectBadge,
  projectLocked,
  onAddProject,
}: BuildSidebarContext): SidebarSection => {
  const resolvedProjectsOptions = projectsOptions.length > 0 ? projectsOptions : allProjectsOptions;
  const hasMultipleOrganisations = organisationsOptions.length > 1;
  const organisationLabel = (() => {
    if (!hasMultipleOrganisations) return 'Workspace';
    if (!selectedOrganisationId) return 'Select Workspace';
    const selectedOrganisation = organisationsOptions.find(
      (org) => org.id === selectedOrganisationId,
    );
    return selectedOrganisation?.name ?? 'Workspace';
  })();
  const organisationItems = organisationsOptions.map((org) => ({
    id: org.id,
    label: org.name,
    onClick: () => {
      setSelectedOrganisationId(org.id);
      setSelectedProjectId(null);
      notify({ message: `Switched to ${org.name}`, tone: 'blue' });
      onNavigate('/execution-runs');
    },
  }));
  const projectItems: Array<{
    id: string;
    label: string;
    onClick?: () => void;
    leadingAdornment?: ReactElement;
    helperText?: string;
  }> = [
    {
      id: 'all-projects',
      label: 'All Projects',
      onClick: () => {
        setSelectedProjectId(null);
        notify({ message: 'Viewing all projects', tone: 'blue' });
        onNavigate('/execution-runs');
      },
    },
    ...resolvedProjectsOptions.map((project) => ({
      id: project.id,
      label: project.name,
      leadingAdornment: project.locked
        ? buildProjectLockAdornment()
        : project.badgeEnabled
          ? buildProjectBadge({ chars: project.badgeChars, color: project.badgeColor })
          : undefined,
      helperText: project.lockReason ?? undefined,
      onClick: () => {
        setSelectedProjectId(project.id);
        notify({
          message: project.locked
            ? `${project.name} is view-only on the current plan`
            : `Switched to ${project.name}`,
          tone: project.locked ? 'orange' : 'blue',
        });
        onNavigate(activePath);
      },
    })),
  ];
  projectItems.push({
    id: 'add-project',
    label: 'Add New',
    ...(canCreateProject ? { onClick: onAddProject } : {}),
  });

  const items: SidebarItem[] = [
    ...(hasMultipleOrganisations
      ? [
          {
            id: 'workspace-organisation',
            label: organisationLabel,
            href: '#organisation',
            popover: {
              items: organisationItems,
            },
          },
        ]
      : []),
    {
      id: 'workspace-project',
      label: projectLabel,
      href: '#projects',
      leadingAdornment: projectLocked
        ? buildProjectLockAdornment()
        : shouldShowProjectBadge(projectLabel, projectBadge)
          ? buildProjectBadge(projectBadge)
          : undefined,
      disabled: lockProjectSelector,
      disabledTone: 'default' as const,
      popover: lockProjectSelector
        ? undefined
        : {
            title: 'Projects',
            items: projectItems,
          },
    },
  ];

  return { title: 'Workspace', items };
};

export const buildSidebarSections = (
  context: BuildSidebarContext,
): { sections: SidebarSection[]; footerItems: SidebarItem[] } => {
  const {
    session,
    navItems,
    activePath,
    onNavigate: _onNavigate,
    preloadRoute,
    addOns,
    isAdmin,
    isSuperuser,
    planName,
    planCode,
    selectedOrganisationId,
    selectedProjectId,
    projectsOptions,
    allProjectsOptions,
  } = context;
  const filteredNav = navItems.filter((item) => {
    if (item.requiresSuperuser) {
      return isSuperuser;
    }
    if (item.requiresAdmin) {
      return isAdmin;
    }
    return true;
  });
  const fallbackOrganisationId = session?.lastOrganisationId ?? session?.orgId ?? null;
  const firstOrganisationId = context.organisationsOptions[0]?.id ?? null;
  const resolvedOrganisationId = firstDefined([
    selectedOrganisationId,
    fallbackOrganisationId,
    firstOrganisationId,
  ]);
  const resolvedProjectsOptions = projectsOptions.length > 0 ? projectsOptions : allProjectsOptions;
  const sessionProjectId = session?.lastProjectId ?? null;
  const firstProjectId = resolvedProjectsOptions[0]?.id ?? null;
  const resolvedProjectId = firstDefined([selectedProjectId, sessionProjectId, firstProjectId]);
  const hasResolvedOrganisation = resolvedOrganisationId !== null;
  const deliveryEnabled = !hasResolvedOrganisation || addOns?.deliveryEnabled === true;
  const operationsEnabled = !hasResolvedOrganisation || addOns?.operationsEnabled === true;
  const codeEnabled = !hasResolvedOrganisation || addOns?.codeEnabled === true;
  const canSeeAdminFooter = isAdmin || isSuperuser;
  const footerItems: SidebarItem[] = [
    ...(canSeeAdminFooter && hasResolvedOrganisation
      ? [
          {
            id: 'organisation-settings',
            label: 'Workspace Settings',
            href: buildOrganisationSettingsHref(resolvedOrganisationId),
            icon: <BusinessIcon fontSize="small" />,
            active: isSelected('/organisation-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/organisation-settings')
              : undefined,
          },
        ]
      : []),
    ...(canSeeAdminFooter && resolvedProjectId
      ? [
          {
            id: 'project-settings',
            label: 'Project Settings',
            href: buildProjectSettingsHref(resolvedProjectId),
            icon: <WorkOutline fontSize="small" />,
            active: isSelected('/project-settings', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/project-settings')
              : undefined,
          },
        ]
      : []),
    ...(canSeeAdminFooter && hasResolvedOrganisation
      ? [
          {
            id: 'plan-settings',
            label: 'Plan Settings',
            href: buildPlanSettingsHref(resolvedOrganisationId),
            active: isSelected('/plan-settings', undefined, activePath),
            rightAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('xs') }}>
                <Chip label={planName ?? 'Loading'} size="xs" tone={getPlanChipTone(planCode)} />
              </Box>
            ),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/plan-settings')
              : undefined,
          },
        ]
      : []),
    ...(canSeeAdminFooter && isSuperuser
      ? [
          {
            id: 'allowed-users',
            label: 'Superuser',
            href: '/superuser',
            active: isSelected('/superuser', undefined, activePath),
            preload: preloadRoute
              ? (): Promise<void> | null => preloadRoute('/superuser')
              : undefined,
          },
        ]
      : []),
  ];
  const baseSections: SidebarSection[] = [
    buildWorkspaceSection(context),
    {
      title: 'Overview',
      items: pickNavItems(filteredNav, ['reports'], activePath, preloadRoute),
    },
    ...(!deliveryEnabled
      ? []
      : [
          {
            title: 'Delivery',
            items: pickNavItems(filteredNav, ['releases', 'features'], activePath, preloadRoute),
          },
        ]),
    ...(!codeEnabled
      ? []
      : [
          {
            title: 'Code',
            items: pickNavItems(
              filteredNav,
              ['coverage-runs', 'coverage-directories', 'coverage-files'],
              activePath,
              preloadRoute,
            ),
          },
        ]),
    {
      title: 'Test',
      items: pickNavItems(
        filteredNav,
        ['execution-runs', 'suites', 'tests'],
        activePath,
        preloadRoute,
      ),
    },
    ...(!operationsEnabled
      ? []
      : [
          {
            title: 'Operations',
            items: pickNavItems(
              filteredNav,
              ['pipelines', 'stages', 'jobs'],
              activePath,
              preloadRoute,
            ),
          },
        ]),
  ];
  const filteredSections = baseSections.filter((section) => {
    if (section.items.length === 0) return false;
    return true;
  });
  return { sections: filteredSections, footerItems };
};
